import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { CRITERION_LABEL_KEY, WRITING_CRITERIA } from '../../constants';
import type { IeltsBandReport } from '../../types';

interface Criterion {
  key: string;
  /** Null until the criterion is scored. */
  band: number | null;
}

interface Block {
  key: string;
  title: string;
  subtitle: string;
  band: number | null;
  criteria: Criterion[];
  status: 'ai' | 'teacher' | null;
  /** The grader's written reason for the band. Absent while the review is locked: the API leaves it out. */
  feedback?: string;
}

const TOP_BAND = 9;

const formatBand = (value: number | null | undefined) => (typeof value === 'number' ? value.toFixed(1) : '—');

/** The criteria at a block's lowest band, or none when every criterion shares one band. */
export function focusCriteria(criteria: Criterion[]): Set<string> {
  const bands = criteria.flatMap((c) => (c.band === null ? [] : [c.band]));
  if (bands.length < 2) return new Set();
  const lowest = Math.min(...bands);
  if (lowest === Math.max(...bands)) return new Set();
  return new Set(criteria.filter((c) => c.band === lowest).map((c) => c.key));
}

/** Per-criterion bands for each Writing task and Speaking, side by side. */
export function IeltsCriteriaBreakdown({ report }: { report: IeltsBandReport }) {
  const { t } = useTranslation();
  const blocks: Block[] = [
    ...(report.writing?.tasks ?? []).map((task) => ({
      key: task.questionId,
      title: t(task.task === 'TASK_1' ? 'exam.writing.task1' : 'exam.writing.task2'),
      subtitle: t('exam.report.task_share', { share: task.task === 'TASK_1' ? '⅓' : '⅔', skill: t('exam.kind.writing') }),
      band: task.band,
      // An unscored task still lists what it will be scored on.
      criteria: task.criteria.length > 0 ? task.criteria : WRITING_CRITERIA[task.task].map((key) => ({ key, band: null })),
      status: task.confirmed ? null : task.aiSuggested ? ('ai' as const) : ('teacher' as const),
      feedback: task.feedback?.trim() || undefined,
    })),
    ...(report.speaking && report.speaking.criteria.length > 0
      ? [{
          key: report.speaking.questionId,
          title: t('exam.kind.speaking'),
          subtitle: t('exam.report.speaking_answers', { count: report.speaking.recordings }),
          band: report.speaking.band,
          criteria: report.speaking.criteria,
          status: report.speaking.pending ? (report.speaking.aiSuggested ? ('ai' as const) : ('teacher' as const)) : null,
        }]
      : []),
  ];
  if (blocks.length === 0) return null;

  // Three blocks sit in two columns on tablets, so the last one takes the full row there.
  const threeUp = blocks.length === 3;
  return (
    <div>
      <div className={cn('grid gap-4 lg:gap-5', blocks.length === 2 && 'md:grid-cols-2', threeUp && 'md:grid-cols-2 lg:grid-cols-3')}>
        {blocks.map((block, index) => {
          const spansRow = threeUp && index === 2;
          return (
            <CriteriaBlock
              key={block.key}
              block={block}
              className={cn(spansRow && 'md:col-span-2 lg:col-span-1')}
              listClassName={cn(
                blocks.length === 1 && 'md:grid-cols-2 md:gap-x-10',
                spansRow && 'md:grid-cols-2 md:gap-x-10 lg:grid-cols-1',
              )}
            />
          );
        })}
      </div>
    </div>
  );
}

function CriteriaBlock({ block, className, listClassName }: { block: Block; className?: string; listClassName?: string }) {
  const { t } = useTranslation();
  const focus = focusCriteria(block.criteria);
  const titleId = `criteria-${block.key}`;

  return (
    <article aria-labelledby={titleId} className={cn('rounded-[22px] border border-[#E6E9F2] bg-card p-[18px] dark:border-border sm:p-6', className)}>
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <h3 id={titleId} className="font-display text-[17px] font-extrabold leading-snug tracking-[-0.03em] text-foreground sm:text-lg">{block.title}</h3>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted-foreground">
            <span>{block.subtitle}</span>
            {block.status && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                {block.status === 'ai' ? t('exam.report.ai_suggested') : t('exam.report.awaiting_teacher')}
              </span>
            )}
          </p>
        </div>
        <p
          className={cn(
            'shrink-0 font-display text-4xl font-extrabold leading-none tracking-[-0.04em] tabular-nums text-[color:var(--ra-fg,#10182B)]',
            block.band === null && '!text-muted-foreground',
          )}
        >
          {formatBand(block.band)}
        </p>
      </header>

      <ul className={cn('mt-6 grid gap-y-4', listClassName)}>
        {block.criteria.map((c) => {
          const isFocus = focus.has(c.key);
          return (
            <li key={c.key}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-foreground">
                  <span>{t(CRITERION_LABEL_KEY[c.key] ?? c.key)}</span>
                  {isFocus && (
                    <span className="rounded-full bg-amber-100 px-2 py-px text-xs font-semibold text-amber-900 dark:bg-amber-400/15 dark:text-amber-300">
                      {t('exam.report.focus_area')}
                    </span>
                  )}
                </span>
                <span
                  className={cn(
                    'shrink-0 text-sm font-semibold tabular-nums',
                    isFocus ? 'text-amber-700 dark:text-amber-300' : c.band === null ? 'text-muted-foreground' : 'text-foreground',
                  )}
                >
                  {formatBand(c.band)}
                </span>
              </div>
              <BandMeter value={c.band ?? 0} focus={isFocus} className="mt-2" />
            </li>
          );
        })}
      </ul>
      {block.feedback && <p className="mt-5 border-t border-[#EEF1F8] pt-4 text-[13px] leading-relaxed text-[#364055] dark:border-border dark:text-foreground/80">{block.feedback}</p>}
    </article>
  );
}

/** A continuous bar scaled to the 9-band range; a half band fills half a step. */
function BandMeter({ value, focus, className }: { value: number; focus: boolean; className?: string }) {
  return (
    <div aria-hidden className={cn('h-1.5 overflow-hidden rounded-full lg:h-[7px]', focus ? 'bg-warning-100 dark:bg-warning-400/15' : 'bg-[#EEF1F8] dark:bg-muted', className)}>
      <span
        className={cn('block h-full rounded-full', focus ? 'bg-warning-500 dark:bg-warning-400' : 'bg-[color:var(--ra-fg,#10182B)]')}
        style={{ width: `${Math.min(100, (value / TOP_BAND) * 100)}%` }}
      />
    </div>
  );
}
